import { useApolloClient } from "@apollo/client";
import { BookmarkRemove as BookmarkRemoveIcon } from "@mui/icons-material";
import { Box, Button, ButtonProps, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  QuoteTemplateUnsubscribeButton_QuoteTemplateFragment,
  useUnsubscribeFromQuoteTemplateMutation,
} from "./QuoteTemplateUnsubscribeButton.generated.js";

interface Props extends ButtonProps {
  doc: QuoteTemplateUnsubscribeButton_QuoteTemplateFragment;
  navigateToLatestSharedVersion: (versionNumber: number) => void;
}

export const QuoteTemplateUnsubscribeButton = ({
  doc,
  navigateToLatestSharedVersion,
  ...rest
}: Props) => {
  const { t } = useTranslate(["Templates", "TemplatesPublishing"]);

  const client = useApolloClient();
  const [unsubscribe, { loading }] = useUnsubscribeFromQuoteTemplateMutation({
    client,
  });

  const tooltip = doc.usedInOtherTemplates
    ? t("Template is used in other templates, cannot unsubscribe", {
        ns: "TemplatesPublishing",
      })
    : "";

  return (
    <Tooltip title={tooltip}>
      <Box display="flex" flexShrink={0} flexGrow={0}>
        <Button
          loading={loading}
          startIcon={
            rest.size !== "extra-small" ? <BookmarkRemoveIcon /> : undefined
          }
          variant={"outlined"}
          color="primary"
          sx={{ whiteSpace: "nowrap" }}
          onClick={async e => {
            e.preventDefault();
            e.stopPropagation();
            const resolvedVersionNumber = doc.resolvedAsReadModelVersionNumber;
            if (doc.usedInOtherTemplates) return;
            if (!doc.templateSharing?.subscribedToTemplate) {
              throw new Error("Template not subscribed");
            }
            const data = await unsubscribe({
              variables: { input: { quoteTemplateId: doc.id } },
            });
            const latestSharedVersionNumber =
              data.data?.unsubscribeFromQuoteTemplate?.quoteTemplate
                ?.templateSharing?.latestSharedVersionNumber;
            if (
              latestSharedVersionNumber &&
              resolvedVersionNumber &&
              latestSharedVersionNumber > resolvedVersionNumber
            ) {
              navigateToLatestSharedVersion(latestSharedVersionNumber);
            } else {
              await client.reFetchObservableQueries();
            }
          }}
          {...rest}
          disabled={doc.usedInOtherTemplates}
        >
          {t("Unsubscribe", { ns: "TemplatesPublishing" })}
        </Button>
      </Box>
    </Tooltip>
  );
};
